import { LM, sideLandmarks, THRESHOLDS } from '@erectus/shared';
import type { GateReason, GateResult, Landmark, Side } from '@erectus/shared';
import { distance, toPlane } from '@/metrics/geometry.js';

/**
 * Quality gate (docs/ARCHITECTURE.md §1.3).
 *
 * Runs on every sampled frame and answers two questions:
 *  1. is this frame measurable at all (side view, ear visible, in frame, lit)?
 *  2. which side of the body faces the camera?
 *
 * Failing frames are recorded as `invalid` and excluded from scoring — they are
 * reported to the user as "not measurable %", never silently dropped.
 */
export interface GateInput {
  landmarks: readonly Landmark[];
  /** How many poses the detector returned this frame. */
  poseCount: number;
  aspect: number;
  /** Mean luma 0–255, or null when luminance sampling is unavailable. */
  meanLuma: number | null;
}

export function qualityGate(input: GateInput): GateResult {
  const { landmarks, poseCount, aspect, meanLuma } = input;
  const reasons: GateReason[] = [];

  const side = detectSide(landmarks);
  const idx = sideLandmarks(side);

  // Light is a property of the picture, not of the person, so it is judged even
  // when nobody is in frame yet.
  const lightReasons: GateReason[] = [];
  if (meanLuma !== null) {
    if (meanLuma < THRESHOLDS.gate.meanLumaMin) lightReasons.push('tooDark');
    else if (meanLuma > THRESHOLDS.gate.meanLumaMax) lightReasons.push('tooBright');
  }

  if (poseCount === 0 || landmarks.length === 0) {
    return {
      ok: false,
      reasons: ['noPose', ...lightReasons],
      side,
      flags: { kneeAvailable: false, hipAvailable: false },
    };
  }
  if (poseCount > 1) reasons.push('multiPerson');

  const leftShoulder = landmarks[LM.LEFT_SHOULDER];
  const rightShoulder = landmarks[LM.RIGHT_SHOULDER];
  const shoulder = landmarks[idx.shoulder];
  const hip = landmarks[idx.hip];
  const ear = landmarks[idx.ear];
  const knee = landmarks[idx.knee];
  const nose = landmarks[LM.NOSE];

  // Side view: in a true profile the two shoulders project onto nearly the same x.
  if (leftShoulder && rightShoulder && shoulder && hip) {
    const torso = distance(toPlane(shoulder, aspect), toPlane(hip, aspect));
    const separation = Math.abs(leftShoulder.x - rightShoulder.x) * aspect;
    const ratio = torso > 1e-6 ? separation / torso : Number.POSITIVE_INFINITY;
    if (ratio >= THRESHOLDS.gate.maxShoulderSeparationRatio) reasons.push('notSideView');
  } else {
    reasons.push('notSideView');
  }

  const earVisible = (ear?.visibility ?? 0) >= THRESHOLDS.gate.earVisibility;
  if (!earVisible) reasons.push('earHidden');

  const hipAvailable = (hip?.visibility ?? 0) >= THRESHOLDS.gate.hipVisibility;
  if (!hipAvailable) reasons.push('hipHidden');

  // Knee is optional: a desk often hides it, so we disable knee/hip-derived
  // metrics instead of failing the frame (PRODUCT §3.1).
  const kneeAvailable = (knee?.visibility ?? 0) >= THRESHOLDS.gate.kneeVisibility;

  if (!nose || nose.y <= THRESHOLDS.gate.minNoseY) reasons.push('outOfFrame');

  reasons.push(...lightReasons);

  return {
    ok: reasons.length === 0,
    reasons,
    side,
    flags: { kneeAvailable, hipAvailable },
  };
}

/**
 * Which side faces the camera.
 * ARCHITECTURE §1.3 uses `x(ear) < x(shoulder) ? 'left' : 'right'`; we pick the
 * side whose ear + hip are actually more visible, which is the same decision in
 * a clean profile and far more stable when one side drops out.
 */
export function detectSide(landmarks: readonly Landmark[]): Side {
  const score = (ear: number, shoulder: number, hip: number): number =>
    (landmarks[ear]?.visibility ?? 0) * 2 +
    (landmarks[shoulder]?.visibility ?? 0) +
    (landmarks[hip]?.visibility ?? 0);

  const leftScore = score(LM.LEFT_EAR, LM.LEFT_SHOULDER, LM.LEFT_HIP);
  const rightScore = score(LM.RIGHT_EAR, LM.RIGHT_SHOULDER, LM.RIGHT_HIP);
  return leftScore >= rightScore ? 'left' : 'right';
}

const REASON_COPY: Record<GateReason, string> = {
  noPose: 'No one is in frame yet.',
  multiPerson: 'More than one person is in frame — the session pauses automatically.',
  notSideView: 'Turn the camera to your side. Both shoulders should overlap.',
  earHidden: 'Your ear is hidden. Tuck long hair behind it so the neck angle can be measured.',
  hipHidden: 'Your hip is hidden. Move the camera slightly forward, up to about 15°.',
  outOfFrame: 'Leave a little space above your head.',
  tooDark: 'It is too dark. Add light on your face and body.',
  tooBright: 'Too much backlight. Avoid sitting with a window behind you.',
};

export function gateReasonText(reason: GateReason): string {
  return REASON_COPY[reason];
}

/**
 * Checklist rows shown live in Capture Setup (PRODUCT §2 screen 4).
 * Every row about the person fails while nobody is detected: "your ear is
 * visible" cannot be true of an empty desk. Only lighting can pass then.
 * `shortLabel` is for the chips floating over the camera preview, where the
 * full sentence would wrap and cover the picture.
 */
export const SETUP_CHECKS = [
  { id: 'side_view', label: 'Camera is at your side', shortLabel: 'Side view', reasons: ['noPose', 'notSideView'] },
  { id: 'ear', label: 'Your ear is visible', shortLabel: 'Ear', reasons: ['noPose', 'earHidden'] },
  { id: 'hip', label: 'Your hip is visible', shortLabel: 'Hip', reasons: ['noPose', 'hipHidden', 'outOfFrame'] },
  { id: 'light', label: 'Lighting is usable', shortLabel: 'Light', reasons: ['tooDark', 'tooBright'] },
  { id: 'multi_person', label: 'Only you are in frame', shortLabel: 'Just you', reasons: ['multiPerson', 'noPose'] },
] as const satisfies ReadonlyArray<{
  id: 'side_view' | 'ear' | 'hip' | 'light' | 'multi_person';
  label: string;
  shortLabel: string;
  reasons: readonly GateReason[];
}>;

export type SetupCheckId = (typeof SETUP_CHECKS)[number]['id'];

export function checkPassed(check: (typeof SETUP_CHECKS)[number], reasons: GateReason[]): boolean {
  return !check.reasons.some((r) => reasons.includes(r));
}
